import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { Box } from '@material-ui/core'
import { useIntl } from 'react-intl'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import CheckboxSendInvitation from './CheckboxSendInvitation'
import {
  changeCSVData,
  setIsOpenDeleteCSVFileDialog,
  useImportCSVData,
  setTags,
} from 'app/store/importCSV'
import { sendSingleRowToValidateThunk } from 'app/store/importCSV/importCSVExtraReducers'
import ArticleIcon from 'app/components/icons/ArticleIcon'
import HeaderSelect from './HeaderSelect/HeaderSelect'
import ErrorMessageBar from './ErrorMessageBar'
import ActionColumn from './ActionColumn'
import ValidateButton from './ValidateButton'
import EditableCell from './EditableCell'
import { usePlace } from 'commons/usePlace'
import {
  STEP_PROGRESS_ONE,
  STEP_PROGRESS_THREE,
  STEP_PROGRESS_TWO,
} from 'app/constants/subscriberCRM'
import ErrorMessageForFields from './ErrorMessageForFields'
import ImportButton from './ImportButton'
import DialogEditCSVDataRow from './DialogEditCSVDataRow'
import DownloadErrorFileBtn from './DownloadErrorFileBtn'
import DialogDeleteCSVFile from './dialogs/DialogDeleteCSVFile'
import DialogImportedSubscribers from './dialogs/DialogImportedSubscribers'
import { createHeaderByIndex } from '../ImportFromCsv/StepOne/helpers'
import debounce from 'lodash/debounce'
import useStyles from './styles'
import GroupsSelector from 'app/componentV4/Filters/GroupsSelector'
import useSubscribers from 'app/main/subscribersV4/useSubscribers'
import { useFormik } from 'formik'
import { useGroups } from 'commons/useGroups'
import { GROUP_CREATED_BY_USER } from 'libs/groups.utils'
import useFeatureFlag from 'app/configs/useFeatureFlag'

const PreviewDataTable = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const [page, setPage] = useState(1)
  const place = usePlace()
  const { formatMessage, locale } = useIntl()
  const { data: CSVFileData, activeStep, optionsLanguages } = useImportCSVData()
  const { headers, rows: data, fileName } = CSVFileData
  const tableContainerRef = useRef(null)
  const { fetchAllGroups } = useGroups()
  const { mes_5172_csv_invite } = useFeatureFlag()
  const groups = useSelector((state) => state.groups.groupList)?.filter(
    (group) => group.type === GROUP_CREATED_BY_USER,
  )
  const resetGroups = () => {}
  const isInviteSubscribers = place?.isInviteSubscribers || false
  useEffect(() => {
    void fetchAllGroups()
  }, [])

  const formik = useFormik({
    initialValues: {
      filterGroups: { groups: [], tags: {} },
    },
  })

  useEffect(() => {
    const allTags =
      Object.values(formik?.values?.filterGroups?.tags).flat() || []
    dispatch(setTags(allTags))
  }, [formik.values])

  const { handleGroups } = useSubscribers({ resetGroups })

  const rowsPerPage = 100

  const isShowErrorsColumn = useMemo(() => {
    return data.some(({ errors }) => !!errors)
  }, [data])

  const columnVisibility = useMemo(
    () => ({
      errors: isShowErrorsColumn,
    }),
    [isShowErrorsColumn],
  )

  const rowsData = useMemo(() => {
    const totalRowCount = data.length
    const currentPageSize = page * rowsPerPage
    const pageSize =
      currentPageSize > totalRowCount ? totalRowCount : currentPageSize
    return data.slice(0, pageSize)
  }, [data, page])

  const nextPage = () => {
    setPage((prevPageNumber) =>
      Math.ceil(data.length / rowsPerPage) === prevPageNumber
        ? prevPageNumber
        : prevPageNumber + 1,
    )
  }

  const defaultColumn = {
    cell: ({ getValue, row: { index, original }, column, table }) => {
      const initialValue = getValue()
      const columnIndex = isShowErrorsColumn
        ? column.getIndex() - 1
        : column.getIndex()
      const changedHeaderkey = headers[columnIndex]?.changedHeaderkey || ''
      const updateData = table.options.meta?.updateData

      return (
        <EditableCell
          id={column.id}
          index={index}
          changedHeaderkey={changedHeaderkey}
          initialValue={initialValue}
          row={original}
          updateData={updateData}
          optionsLanguages={optionsLanguages}
        />
      )
    },
  }

  const getCommonPinningStyles = (column) => {
    const isPinned = column.getIsPinned()

    const isActionColumn = column.id === 'action'

    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left')
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right')

    return {
      boxShadow: isLastLeftPinnedColumn
        ? '4px 0 0px 0px rgba(0, 0, 0, 0.09)'
        : isFirstRightPinnedColumn
          ? '-4px 0 0px 0px rgba(0, 0, 0, 0.09)'
          : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? 'sticky' : 'relative',
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
      flex: isActionColumn ? 1 : 'none',
    }
  }

  const setPinningActionColumn = (containerElement) => {
    const { scrollLeft, offsetWidth, scrollWidth } = containerElement
    const isScrolledToStart = Math.round(scrollLeft) === 0
    const isScrolledToEnd = Math.round(scrollLeft) + offsetWidth === scrollWidth

    table.setColumnPinning({
      left: !isScrolledToStart ? ['errors'] : [],
      right: !isScrolledToEnd ? ['action'] : [],
    })
  }

  const fetchMoreOnBottomReached = useCallback((containerRefElement) => {
    if (containerRefElement) {
      setPinningActionColumn(containerRefElement)
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement
      if (scrollHeight - scrollTop - clientHeight < 100) {
        nextPage()
      }
    }
  }, [])

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current)
  }, [fetchMoreOnBottomReached])

  const memoizedColumns = useMemo(() => {
    const columns = headers.map((column, index) => {
      const { isInvalid, headerKey, changedHeaderkey, label } = column
      return {
        accessorKey: headerKey || createHeaderByIndex(index),
        header: (props) => {
          const { header } = props
          return (
            <HeaderSelect
              initialValue={header.id}
              isInvalid={isInvalid}
              label={formatMessage({ id: label })}
              changedHeaderkey={changedHeaderkey}
              index={index}
            />
          )
        },
      }
    })
    columns.unshift({
      accessorKey: 'errors',
      enablePinning: true,
      cell: (props) => {
        const { errors } = props?.row?.original || {}
        return errors ? <ErrorMessageForFields errors={errors} /> : null
      },
      header: () => <div />,
      pin: 'left',
      size: 40,
    })
    columns.push({
      accessorKey: 'action',
      enablePinning: true,
      cell: (props) => <ActionColumn index={props.row.index} />,
      header: () => <div />,
      pin: 'right',
      size: 40,
      style: { flex: 1 },
    })
    return columns
  }, [headers, locale])

  const debouncedUpdateData = useMemo(() => {
    return debounce(({ changedSubscriber, index, request }) => {
      if (activeStep === STEP_PROGRESS_TWO) {
        dispatch(changeCSVData({ changedSubscriber, index }))
      } else if (activeStep === STEP_PROGRESS_THREE) {
        request.sendToValidateRequest = dispatch(
          sendSingleRowToValidateThunk({ changedSubscriber, index }),
        )
      }
    }, 800) // debounce delay in ms
  }, [activeStep, dispatch])

  const table = useReactTable({
    data: rowsData,
    columns: memoizedColumns,
    defaultColumn,
    initialState: {
      columnPinning: {
        right: ['action'],
      },
    },
    state: {
      columnVisibility,
    },

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      updateData: debouncedUpdateData,
    },
  })

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 56,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 7,
  })

  const removeCSVFile = () => {
    dispatch(setIsOpenDeleteCSVFileDialog(true))
  }

  return (
    <div className={classes.previewDataTable}>
      <ErrorMessageBar />
      <div className={classes.toolbar}>
        <div className={classes.fileNameWrapp}>
          <ArticleIcon />
          <p className={classes.fileName}>{fileName}</p>
        </div>

        <p className={classes.deleteBtn} onClick={removeCSVFile}>
          <FormattedMessage id="action.delete" />
        </p>
      </div>
      <div
        className={classes.tableContainer}
        onScroll={(e) => fetchMoreOnBottomReached(e.target)}
        ref={tableContainerRef}
      >
        <table className="grid">
          <thead className="grid sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="flex w-full">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      style={{
                        width: header.getSize(),
                        ...getCommonPinningStyles(header.column),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody
            className="grid relative"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              return (
                <tr
                  data-index={virtualRow.index}
                  ref={(node) => rowVirtualizer.measureElement(node)}
                  key={row.id}
                  className="flex absolute w-full"
                  style={{
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {row?.getVisibleCells().map((cell) => {
                    return (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          ...getCommonPinningStyles(cell.column),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {activeStep !== STEP_PROGRESS_ONE && (
        <div className={classes.actionButtonsWrapp}>
          {activeStep === STEP_PROGRESS_TWO && <ValidateButton />}

          {activeStep === STEP_PROGRESS_THREE && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-end"
              width="100%"
              flexWrap="wrap"
            >
              {groups?.length ? (
                <GroupsSelector
                  name="filterGroups"
                  disabled={!groups?.length}
                  className="mt-5 mr-5 bg-primary-200 text-primary text-14 font-normal"
                  value={formik.values.filterGroups}
                  onChange={(value) => {
                    formik.setFieldValue('filterGroups', value)
                    handleGroups(value)
                  }}
                  isImportedPage={true}
                />
              ) : (
                <div></div>
              )}

              <Box
                display={'flex'}
                alignItems={'centre'}
                flexWrap={'wrap'}
                justifyContent={'flex-end'}
              >
                <DownloadErrorFileBtn />
                <ImportButton />
              </Box>
              {isInviteSubscribers && mes_5172_csv_invite && (
                <Box width="100%" display="flex" justifyContent="flex-end">
                  <CheckboxSendInvitation />
                </Box>
              )}
            </Box>
          )}
        </div>
      )}
      <DialogEditCSVDataRow />
      <DialogDeleteCSVFile />
      <DialogImportedSubscribers />
    </div>
  )
}

export default PreviewDataTable
